// ============================================================
// useCalendarInteractionV2 — Canvas DnD の Pointer Events 実装
//
// interactionMachine（純粋 reducer）に正規化イベントを流し、
// 返ってきた effect を解釈するだけの薄い hook。
// 旧 useCalendarInteraction との違い:
//   - mouse イベント → Pointer Events + setPointerCapture
//     （画面外リリースの取りこぼしと lastBodyY ワークアラウンドを構造的に解消。
//       コンテナ外座標のフォールバックは保険として維持）
//   - pointercancel を 'cancel' として処理（旧実装に無かった安全弁。
//     タッチのスクロールジェスチャ等でブラウザがポインタを奪っても idle に戻る）
//   - 状態は machine の単一 discriminated union（ゴースト残留バグの構造的排除）
//   - clearTransientGhosts は進行中ジェスチャ自体も破棄する（Escape = 真のキャンセル）
//
// 返却インターフェース・ジェスチャ挙動は旧実装と同一
// （仕様: md/master_md/page-calendar.md「DnDジェスチャ仕様」）。
// 旧実装へのフォールバック: localStorage.__calendar_dnd_v1__ = '1'（GridV2 参照）
//
// ※ touch-action は変更しない: タッチでのスクロールは従来どおりブラウザに任せ、
//   その場合 pointercancel で安全に idle へ戻る。タッチDnDの正式対応は Phase 3。
// ============================================================

import { useEffect, useState, useCallback, useRef } from 'react';
import { GRID_CONFIG } from './types';
import { GhostPosition, DISPLAY_STATUSES } from './GridCanvas';
import { absoluteYToDate, viewToBody } from './useGridGeometry';
import { hitTestSmallestBlock } from './hitTest';
import {
  IDLE,
  MachineEvent,
  MachineEffect,
  MachineState,
  PressTarget,
  transition,
} from './interactionMachine';
import {
  UseCalendarInteractionProps,
  UseCalendarInteractionReturn,
  ResizeGhost,
} from './useCalendarInteraction';

const { columnWidth, headerHeight, leftGutterWidth, overlayMinHeight } = GRID_CONFIG;

// タッチ対応 (2026-07-03 Phase 3):
//   長押しでジェスチャ開始、それ以前の移動はスクロールとしてブラウザに委譲。
//   touch-action は変更しない（スクロール共存のため）。長押し成立後は
//   非 passive touchmove の preventDefault でスクロール開始を抑止する。
/** 長押し判定時間（ms） */
export const TOUCH_LONG_PRESS_MS = 400;
/** 長押し前にこれ以上動いたらスクロール扱い（|dx|+|dy| px） */
export const TOUCH_MOVE_SLOP_PX = 10;

interface PendingTouch {
  pointerId: number;
  clientX: number;
  clientY: number;
  target: PressTarget;
  downEvent: PointerEvent;
  timer: ReturnType<typeof setTimeout>;
}

export function useCalendarInteractionV2({
  containerRef,
  vehicles,
  reservations,
  rangeStart,
  onReservationClick,
  onEmptyCellClick,
  onCmdReservationClick,
  onReservationDblClick,
  onHeaderClick,
  onDragCreate,
  onReservationResize,
  interactionMode = 'none',
  selectedBlockHeight = 0,
  selectedAbsoluteY = 0,
  hoveredReservationId,
  zoom = 1,
  onHeaderDragStart,
  onHeaderDragOver,
  onHeaderDrop,
  onHeaderDragEnd,
  enabled = true,
}: UseCalendarInteractionProps): UseCalendarInteractionReturn {
  const [ghostPosition, setGhostPosition] = useState<GhostPosition | null>(null);
  const [dragGhost, setDragGhost] = useState<GhostPosition | null>(null);
  const [resizeGhost, setResizeGhost] = useState<ResizeGhost | null>(null);
  const ghostRafRef = useRef<number>(0);

  // machine 状態と追跡中ポインタは ref（リスナー再バインドを跨いで永続）
  const machineRef = useRef<MachineState>(IDLE);
  const activePointerRef = useRef<number | null>(null);
  // タッチ長押し待ち（この間のスクロールはブラウザに委譲）
  const pendingTouchRef = useRef<PendingTouch | null>(null);
  // タッチ起点のジェスチャ進行中（touchmove preventDefault の判定用）
  const touchDragActiveRef = useRef(false);
  // 長押しタイマーから最新バインドのジェスチャ開始処理を呼ぶための間接参照
  // （hover 変化等でリスナーが再バインドされても長押しが壊れないように）
  const startTouchGestureRef = useRef<((pending: PendingTouch) => void) | null>(null);

  // 押下〜リリースのジェスチャ処理（Pointer Events + machine）
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    /** ヘッダー列ドロップ位置（列境界、[0, vehicles.length] にクランプ） */
    const computeHeaderCol = (event: PointerEvent, rect: DOMRect): number => {
      const zVX = (event.clientX - rect.left) / zoom;
      const scrollX = container.scrollLeft / zoom;
      const hBodyX = zVX - leftGutterWidth + Math.max(0, scrollX - leftGutterWidth);
      return Math.max(0, Math.min(vehicles.length, Math.round(hBodyX / columnWidth)));
    };

    /** pointerdown 位置から押下対象を決定（座標変換 + ヒットテスト） */
    const computePressTarget = (event: PointerEvent): PressTarget => {
      const rect = container.getBoundingClientRect();
      const viewX = event.clientX - rect.left;
      const viewY = event.clientY - rect.top;
      const zVX = viewX / zoom;
      const zVY = viewY / zoom;
      const onHeader = zVX > leftGutterWidth && zVY < headerHeight;

      if (onHeader) {
        if (onHeaderDragStart) {
          const scrollX = container.scrollLeft / zoom;
          const hBodyX = zVX - leftGutterWidth + Math.max(0, scrollX - leftGutterWidth);
          const hvi = Math.floor(hBodyX / columnWidth);
          if (hvi >= 0 && hvi < vehicles.length) {
            return { type: 'headerDrag', fromIndex: hvi };
          }
        }
        return { type: 'click', onHeader: true };
      }
      if (zVX < leftGutterWidth || zVY < headerHeight) return { type: 'click', onHeader: false };
      // 移動/入替モード中はドラッグ作成しない（クリックで移動先を確定する）
      if (interactionMode !== 'none') return { type: 'click', onHeader: false };

      const { bodyX, bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom);
      const vi = Math.floor(bodyX / columnWidth);
      if (vi < 0 || vi >= vehicles.length) return { type: 'click', onHeader: false };

      const vid = String(vehicles[vi].id);
      const hit = hitTestSmallestBlock(reservations, vid, rangeStart, bodyY, DISPLAY_STATUSES);
      if (hit?.edge) {
        return {
          type: 'resize',
          reservation: hit.reservation,
          edge: hit.edge,
          origAbsTop: hit.absTop,
          origAbsBottom: hit.absTop + hit.blockHeight,
          vehicleIndex: vi,
        };
      }
      if (hit) return { type: 'click', onHeader: false }; // 予約中央 → クリック解決のみ
      return { type: 'dragCreate', vehicleIndex: vi, startAbsY: bodyY };
    };

    /** クリック解決（旧実装 resolveClick と同一挙動） */
    const resolveClick = (event: PointerEvent, onHeader: boolean) => {
      const rect = container.getBoundingClientRect();
      const viewX = event.clientX - rect.left;
      const viewY = event.clientY - rect.top;
      const scrollX = container.scrollLeft;
      const scrollY = container.scrollTop;
      const zViewX = viewX / zoom;
      const zViewY = viewY / zoom;
      const zScrollX = scrollX / zoom;

      if (zViewX < leftGutterWidth) return;

      // ヘッダークリック — pointerdown もヘッダー上だった場合のみ
      if (zViewY < headerHeight && onHeader) {
        if (!onHeaderClick) return;
        const bodyX = zViewX - leftGutterWidth + Math.max(0, zScrollX - leftGutterWidth);
        const vehicleIndex = Math.floor(bodyX / columnWidth);
        if (vehicleIndex >= 0 && vehicleIndex < vehicles.length) {
          onHeaderClick(vehicles[vehicleIndex], event.shiftKey, event.metaKey || event.ctrlKey);
        }
        return;
      }
      if (zViewY < headerHeight) return;

      const { bodyX, bodyY } = viewToBody(viewX, viewY, scrollX, scrollY, zoom);
      const vehicleIndex = Math.floor(bodyX / columnWidth);
      if (vehicleIndex < 0 || vehicleIndex >= vehicles.length) return;
      const vehicle = vehicles[vehicleIndex];
      if (!vehicle) return;
      const vehicleId = String(vehicle.id);

      // hover で特定済みの予約を優先使用（Canvas のヒットテストと完全一致）
      let clickedReservation = hoveredReservationId
        ? reservations.find(r => String(r.id) === hoveredReservationId) || null
        : null;
      // タッチには hover が無い（compat mousemove は touchend 後に発火するため
      // この時点では古い）→ タッチ限定で hitTest フォールバック。
      // マウスは従来どおり hover のみ（カスケードX判定との整合を保つ）
      if (!clickedReservation && event.pointerType === 'touch') {
        const hit = hitTestSmallestBlock(reservations, vehicleId, rangeStart, bodyY, DISPLAY_STATUSES);
        clickedReservation = hit?.reservation ?? null;
      }
      if (clickedReservation) {
        if (event.altKey && onCmdReservationClick) {
          event.preventDefault();
          onCmdReservationClick(clickedReservation, false, true);
        } else if (event.shiftKey && onCmdReservationClick) {
          onCmdReservationClick(clickedReservation, true, false);
        } else {
          onReservationClick(clickedReservation);
        }
        return;
      }

      const clickedDate = absoluteYToDate(rangeStart, bodyY);
      onEmptyCellClick(vehicleId, clickedDate);
    };

    /** machine が返した effect を解釈して実行 */
    const applyEffects = (effects: MachineEffect[], event: PointerEvent) => {
      for (const ef of effects) {
        switch (ef.type) {
          case 'dragGhost': {
            const startY = Math.min(ef.startAbsY, ef.currentAbsY);
            const endY = Math.max(ef.startAbsY, ef.currentAbsY);
            setDragGhost({
              vehicleIndex: ef.vehicleIndex,
              absoluteY: startY,
              blockHeight: Math.max(overlayMinHeight, endY - startY),
            });
            break;
          }
          case 'resizeGhost': {
            const newTop = ef.edge === 'top' ? Math.min(ef.bodyY, ef.origAbsBottom - overlayMinHeight) : ef.origAbsTop;
            const newBottom = ef.edge === 'bottom' ? Math.max(ef.bodyY, ef.origAbsTop + overlayMinHeight) : ef.origAbsBottom;
            setResizeGhost({
              reservation: ef.reservation,
              edge: ef.edge,
              absTop: newTop,
              absBottom: newBottom,
              vehicleIndex: ef.vehicleIndex,
            });
            break;
          }
          case 'clearGhosts':
            setDragGhost(null);
            setResizeGhost(null);
            break;
          case 'headerDragStart':
            onHeaderDragStart?.(ef.fromIndex);
            break;
          case 'headerDragOver':
            onHeaderDragOver?.(ef.targetCol);
            break;
          case 'headerDrop':
            onHeaderDrop?.(ef.fromIndex, ef.toCol);
            break;
          case 'headerDragEnd':
            onHeaderDragEnd?.();
            break;
          case 'commitResize': {
            if (!onReservationResize) break;
            const clampedY = ef.edge === 'top'
              ? Math.min(ef.bodyY, ef.origAbsBottom - overlayMinHeight)
              : Math.max(ef.bodyY, ef.origAbsTop + overlayMinHeight);
            onReservationResize(ef.reservation, ef.edge, absoluteYToDate(rangeStart, clampedY));
            break;
          }
          case 'commitDragCreate': {
            if (!onDragCreate) break;
            const startY = Math.min(ef.startAbsY, ef.endAbsY);
            const endY = Math.max(ef.startAbsY, ef.endAbsY);
            if (ef.vehicleIndex >= 0 && ef.vehicleIndex < vehicles.length) {
              const vehicleId = String(vehicles[ef.vehicleIndex].id);
              const startDate = absoluteYToDate(rangeStart, startY);
              const endDate = absoluteYToDate(rangeStart, endY);
              if (endDate > startDate) {
                onDragCreate(vehicleId, startDate, endDate);
              }
            }
            break;
          }
          case 'resolveClick':
            resolveClick(event, ef.onHeader);
            break;
        }
      }
    };

    const dispatch = (ev: MachineEvent, pointerEvent: PointerEvent) => {
      const { next, effects } = transition(machineRef.current, ev);
      machineRef.current = next;
      applyEffects(effects, pointerEvent);
    };

    // 長押し成立 → ジェスチャ開始（タイマーからは ref 経由で常に最新バインドを呼ぶ）
    startTouchGestureRef.current = (pending: PendingTouch) => {
      if (!container.isConnected) return;
      pendingTouchRef.current = null;
      activePointerRef.current = pending.pointerId;
      touchDragActiveRef.current = true;
      const target = pending.downEvent.target as Element;
      try { target.setPointerCapture(pending.pointerId); } catch { /* no-op */ }
      try { (navigator as any).vibrate?.(15); } catch { /* no-op */ }
      dispatch({ type: 'down', clientX: pending.clientX, clientY: pending.clientY, target: pending.target }, pending.downEvent);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      // Canvas 要素以外からのイベントは無視（UI オーバーレイからの伝播防止）
      const target = event.target as HTMLElement;
      if (target.tagName !== 'CANVAS') return;
      // 既にジェスチャ追跡中の別ポインタ（多重タッチ等）は無視
      if (activePointerRef.current !== null || pendingTouchRef.current !== null) return;

      if (event.pointerType === 'touch') {
        // タッチは即ジェスチャ化しない: 長押し(400ms)で開始、それ以前の移動はスクロール
        const pressTarget = computePressTarget(event);
        const pending: PendingTouch = {
          pointerId: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
          target: pressTarget,
          downEvent: event,
          timer: setTimeout(() => {
            const p = pendingTouchRef.current;
            if (p && p.pointerId === event.pointerId) startTouchGestureRef.current?.(p);
          }, TOUCH_LONG_PRESS_MS),
        };
        pendingTouchRef.current = pending;
        return;
      }

      activePointerRef.current = event.pointerId;
      // キャプチャで画面外まで move/up を受け取る（非対応環境は window リスナで補完）
      try { target.setPointerCapture(event.pointerId); } catch { /* no-op */ }
      dispatch({ type: 'down', clientX: event.clientX, clientY: event.clientY, target: computePressTarget(event) }, event);
    };

    const onPointerMove = (event: PointerEvent) => {
      // タッチ長押し待ち: スロップ超過はスクロール操作とみなしジェスチャ化を諦める
      const pending = pendingTouchRef.current;
      if (pending && pending.pointerId === event.pointerId) {
        const dist = Math.abs(event.clientX - pending.clientX) + Math.abs(event.clientY - pending.clientY);
        if (dist > TOUCH_MOVE_SLOP_PX) {
          clearTimeout(pending.timer);
          pendingTouchRef.current = null;
        }
        return;
      }
      if (activePointerRef.current !== event.pointerId) return;
      if (machineRef.current.kind === 'idle') return;
      const rect = container.getBoundingClientRect();
      const viewX = event.clientX - rect.left;
      const viewY = event.clientY - rect.top;
      const { bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom);
      dispatch({ type: 'move', clientX: event.clientX, clientY: event.clientY, bodyY, headerCol: computeHeaderCol(event, rect) }, event);
    };

    const onPointerUp = (event: PointerEvent) => {
      // 長押し前に離した = タップ → down/up を連続 dispatch してクリック解決
      const pending = pendingTouchRef.current;
      if (pending && pending.pointerId === event.pointerId) {
        clearTimeout(pending.timer);
        pendingTouchRef.current = null;
        const rect = container.getBoundingClientRect();
        const { bodyY } = viewToBody(event.clientX - rect.left, event.clientY - rect.top, container.scrollLeft, container.scrollTop, zoom);
        dispatch({ type: 'down', clientX: pending.clientX, clientY: pending.clientY, target: pending.target }, pending.downEvent);
        dispatch({ type: 'up', bodyY, headerCol: computeHeaderCol(event, rect) }, event);
        return;
      }
      if (activePointerRef.current !== event.pointerId) return;
      activePointerRef.current = null;
      touchDragActiveRef.current = false;
      const rect = container.getBoundingClientRect();
      const viewX = event.clientX - rect.left;
      const viewY = event.clientY - rect.top;
      let bodyY: number | null;
      if (viewY < 0 || viewY > rect.height || viewX < 0 || viewX > rect.width) {
        bodyY = null; // コンテナ外リリース → machine が保持する lastBodyY を使用
      } else {
        ({ bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom));
      }
      dispatch({ type: 'up', bodyY, headerCol: computeHeaderCol(event, rect) }, event);
    };

    const onPointerCancel = (event: PointerEvent) => {
      const pending = pendingTouchRef.current;
      if (pending && pending.pointerId === event.pointerId) {
        clearTimeout(pending.timer);
        pendingTouchRef.current = null;
        return;
      }
      if (activePointerRef.current !== event.pointerId) return;
      activePointerRef.current = null;
      touchDragActiveRef.current = false;
      dispatch({ type: 'cancel' }, event);
    };

    // タッチジェスチャ進行中はブラウザのスクロール開始を抑止
    // （touch-action は変更しないため、通常のスクロールは従来どおり動く）
    const onTouchMove = (e: TouchEvent) => {
      if (touchDragActiveRef.current) e.preventDefault();
    };

    container.addEventListener('pointerdown', onPointerDown);
    // キャプチャ中のイベントは capture 要素から bubble するため window で受けられる。
    // capture 失敗時（古い環境）も window リスナなら画面外 move/up を拾える。
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      container.removeEventListener('touchmove', onTouchMove);
      // 長押しタイマーは意図的にクリアしない（hover 変化等の再バインドで長押しが
      // 壊れないように）。タイマーは ref 経由で常に最新バインドの処理を呼ぶ。
      startTouchGestureRef.current = null;
    };
  }, [containerRef, vehicles, reservations, rangeStart, onReservationClick, onEmptyCellClick, onCmdReservationClick, onHeaderClick, onDragCreate, onReservationResize, interactionMode, zoom, hoveredReservationId, onHeaderDragStart, onHeaderDragOver, onHeaderDrop, onHeaderDragEnd, enabled]);

  // ダブルクリック → 予約編集パネル（旧実装と同一。dblclick は pointer 化後も発火する）
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onReservationDblClick || !enabled) return;
    const onDblClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName !== 'CANVAS') return;
      const rect = container.getBoundingClientRect();
      const viewX = event.clientX - rect.left;
      const viewY = event.clientY - rect.top;
      if (viewX / zoom < leftGutterWidth || viewY / zoom < headerHeight) return;
      const { bodyX, bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom);
      const vi = Math.floor(bodyX / columnWidth);
      if (vi < 0 || vi >= vehicles.length) return;
      const vid = String(vehicles[vi].id);
      // hover で特定済みの予約を優先使用
      if (hoveredReservationId) {
        const hoveredR = reservations.find(r => String(r.id) === hoveredReservationId);
        if (hoveredR) {
          event.preventDefault();
          onReservationDblClick(hoveredR);
          return;
        }
      }
      const dblHit = hitTestSmallestBlock(reservations, vid, rangeStart, bodyY, DISPLAY_STATUSES);
      if (dblHit) {
        event.preventDefault();
        onReservationDblClick(dblHit.reservation);
      }
    };
    container.addEventListener('dblclick', onDblClick);
    return () => container.removeEventListener('dblclick', onDblClick);
  }, [containerRef, vehicles, reservations, rangeStart, onReservationDblClick, zoom, hoveredReservationId, enabled]);

  // マウス追従ゴースト（移動/入替/車両スワップモード）（旧実装と同一）
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const isReservationMode = (interactionMode === 'move' || interactionMode === 'move-free' || interactionMode === 'swap') && selectedBlockHeight > 0;
    const isVehicleSwapMode = interactionMode === 'vehicle-swap';
    if (!isReservationMode && !isVehicleSwapMode) {
      setGhostPosition(null);
      return;
    }

    // 自由移動: 最初のマウス位置からのオフセットを記録
    let initialOffsetY: number | null = null;

    const onMouseMove = (event: MouseEvent) => {
      cancelAnimationFrame(ghostRafRef.current);
      ghostRafRef.current = requestAnimationFrame(() => {
        const rect = container.getBoundingClientRect();
        const viewX = event.clientX - rect.left;
        const viewY = event.clientY - rect.top;
        const zViewX = viewX / zoom;

        if (zViewX < leftGutterWidth) {
          setGhostPosition(null);
          return;
        }

        // vehicle-swap モード: ヘッダー上でも動作（bodyX のみ使用）
        const zScrollX = container.scrollLeft / zoom;
        const bodyX = zViewX - leftGutterWidth + Math.max(0, zScrollX - leftGutterWidth);
        const vehicleIndex = Math.floor(bodyX / columnWidth);
        if (vehicleIndex < 0 || vehicleIndex >= vehicles.length) {
          setGhostPosition(null);
          return;
        }

        if (isVehicleSwapMode) {
          // ヘッダーゴースト: vehicleIndex のみ使用
          setGhostPosition({ vehicleIndex, absoluteY: 0, blockHeight: 0 });
          return;
        }

        if (viewY / zoom < headerHeight) {
          setGhostPosition(null);
          return;
        }

        const { bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom);

        if (interactionMode === 'move-free') {
          if (initialOffsetY === null) {
            initialOffsetY = bodyY - selectedAbsoluteY;
          }
          setGhostPosition({
            vehicleIndex,
            absoluteY: bodyY - initialOffsetY,
            blockHeight: selectedBlockHeight,
          });
        } else {
          setGhostPosition({
            vehicleIndex,
            absoluteY: selectedAbsoluteY,
            blockHeight: selectedBlockHeight,
          });
        }
      });
    };

    const onMouseLeave = () => setGhostPosition(null);

    container.addEventListener('mousemove', onMouseMove, { passive: true });
    container.addEventListener('mouseleave', onMouseLeave);
    return () => {
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseleave', onMouseLeave);
      cancelAnimationFrame(ghostRafRef.current);
    };
  }, [containerRef, vehicles, interactionMode, selectedBlockHeight, selectedAbsoluteY, zoom, enabled]);

  /**
   * 外部から強制的にすべての一時ゴーストと進行中ジェスチャをクリアする。
   * Escape ハンドラ等から呼ばれる。V1 と異なりジェスチャ状態そのものも破棄する
   * （= 真のキャンセル。armed 状態が持ち越されない）。
   */
  const clearTransientGhosts = useCallback(() => {
    machineRef.current = IDLE;
    activePointerRef.current = null;
    if (pendingTouchRef.current) {
      clearTimeout(pendingTouchRef.current.timer);
      pendingTouchRef.current = null;
    }
    touchDragActiveRef.current = false;
    setDragGhost(null);
    setResizeGhost(null);
  }, []);

  return { ghostPosition, dragGhost, resizeGhost, clearTransientGhosts };
}
